/**
 * Round-4 QA fixes, Unit U5 (S1) / Key Technical Decisions 6-7 — pure rules
 * for "does saving this sequence need a confirmation dialog, and what should
 * that dialog's copy say".
 *
 * Plan 2026-09-28-006 (rulebook R5, R6) — doc-only pass: these predicates are
 * already correct and unchanged by this plan ("gate by use, never by
 * ownership" — R5's own invariant). What changed is what a gated save shows:
 * every host now opens the ONE Save menu (`usage-sidebar-logic.ts#saveMenuOptions`,
 * R6) instead of a bespoke two-button dialog, and the header/description text
 * comes from `usageHeadline`/`saveMenuOptions` rather than `sidebarCopy`
 * (deprecated).
 *
 * Plan 2026-09-28-007 U7 (rulebook R5, R14, Class E) —
 * `usageGateNeedsConfirmation` (the RUNG doorway's boolean predicate) is
 * DELETED and replaced by {@link inPlayUsageGate}: a gated save is still
 * exactly "more than one (group, play) pair, or more than one unsent play",
 * but the affected set now also has to CONTAIN the group the save was
 * composed from, not just be counted — see that function's own doc for the
 * defect this closes (Codex c3-2). `libraryUsageGateNeedsConfirmation` is
 * unchanged: the Library doorway has no origin group of its own to go stale.
 *
 * The pre-existing gate (round-3-QA-fixes, decision 9) only ever asked
 * "how many RUNGS map this sequence" (`places.length`). That undercounts the
 * real blast radius of a shared-save: a playbook can carry several DRAFT
 * plays against the same rung/head, and every one of them silently receives
 * whatever a "confirm" save writes — a rung is not a play. This module adds
 * the second axis (unsent plays) WITHOUT weakening the first: either axis
 * alone can trigger the gate, and neither axis alone can suppress it.
 *
 * `unsentPlayCount` on `PlaybooksUsagePlace` (see `usage.ts#isUnsentPlay` and
 * `PlaybooksUsagePlaceSchema`'s own doc) is a PLAYBOOK-level count, computed
 * once per playbook and copied onto every place row that playbook produces —
 * two rungs on the same playbook share the exact same plays, so summing
 * `unsentPlayCount` per PLACE (rather than per distinct playbook) would
 * double-count. `distinctUnsentPlays` is written to be immune to that: it
 * takes the last (equivalently, any — they all agree) `unsentPlayCount` seen
 * per `playbookId` and sums across playbooks only.
 *
 * Plan 2026-09-27-006 U4 — `places` and each `unsentPlayCount` come from
 * `usage.ts#usageAxes`: a play counts iff its OWN pinned ladder references
 * the piece, and `places` includes play-only places (a play's ladder still
 * using it at a rung the template remapped away). The server's save-gate
 * rechecks (`api.ts#countSequenceSharedSaveAxes`, `#countSegmentationUsagePlaces`)
 * call the same `usageAxes` over the same two sources (template heads + every
 * non-archived play's own pinned ladder) and compare, on BOTH axes and for
 * both kinds, `usageAxes(...).places.length` against the `places.length`
 * sent from here and the summed `unsentPlayCountByPlaybookId` against
 * `distinctUnsentPlays(places)` (plan 2026-09-27-007 U2 aligned the sequence
 * place count, which used to recheck template rungs only) — one rule, never a
 * separate one.
 */
import type { PlaybooksUsagePlace } from './schemas';

/**
 * Sum of `unsentPlayCount` across DISTINCT `playbookId`s in `places` — never
 * per-place, which would multiply a playbook's own play count by however
 * many rungs on it map this sequence/segmentation.
 */
export function distinctUnsentPlays(places: readonly PlaybooksUsagePlace[]): number {
  const unsentPlayCountByPlaybookId = new Map<string, number>();
  for (const place of places) {
    unsentPlayCountByPlaybookId.set(place.playbookId, place.unsentPlayCount);
  }
  let total = 0;
  for (const count of unsentPlayCountByPlaybookId.values()) {
    total += count;
  }
  return total;
}

/**
 * Plan 2026-09-28-007 U7 (rulebook R5, R14, Class E) — replaces
 * `usageGateNeedsConfirmation` (deleted). The old predicate asked only "how
 * many places" (`places.length > 1`); it never checked WHICH places, so one
 * place that was a DIFFERENT group than the one being saved from still read
 * as "sole use → silent" (Codex c3-2: a stale save landed with no menu, no
 * warning). This answers three ways instead of a boolean:
 *
 *  - `'stale-origin'` — the group the save was composed FROM
 *    (`origin.playbookId` + `origin.rungId`, exactly how `usage.ts#usageAxes`
 *    keys a place — and, when `origin.playId` is given, that play must also
 *    be among the matched place's `plays`) is no longer among `places` at
 *    all: someone moved, remapped or removed that group since the rep
 *    loaded the page. There is nothing to gate on — the write is refused
 *    before it's attempted, not silently mis-saved and not offered a menu
 *    that would act on the wrong group.
 *  - `'silent'` — the origin group is the ONLY place, and no other unsent
 *    play would receive the write (`distinctUnsentPlays(places) <= 1`) —
 *    today's main-line save (round-3-QA-fixes `:974-979` "never become
 *    confirm-every-save").
 *  - `'menu'` — anything else: the origin group is confirmed current, but
 *    at least one other place or unsent play shares the piece.
 *
 * `places.length === 0` depends on the doorway (plan 2026-09-28-008 U2,
 * Codex round-3 F6):
 *  - IN A PLAY (`origin.playId` given) it is `'stale-origin'`: a saved group
 *    of a play that uses the piece is always one of its places (`usageAxes`
 *    lists play-only places too), so an empty list means this group no
 *    longer uses it — refused, never a silent write onto a piece the group
 *    left. (The server's group check on the write is the backstop.)
 *  - on the playbook doorway (no `playId`) it stays `'silent'`: an empty
 *    list is not evidence the template group moved (a freshly-created
 *    identity with no ladder pin resolved to it yet is the ordinary case).
 *
 * Otherwise `'stale-origin'` fires when NONE of `places` is the origin — the
 * precise shape of the Codex c3-2 repro (one place, and that one place is a
 * DIFFERENT group).
 *
 * `origin.rungId` is a plain `string`, never `null` — a rung staged this
 * session with no server id yet cannot be matched against `places` (which
 * only ever lists SAVED rungs), so every caller resolves that case (usually
 * `SAVE_GROUP_FIRST`) before calling this, not inside it.
 */
export function inPlayUsageGate(
  places: readonly PlaybooksUsagePlace[],
  origin: { playbookId: string; rungId: string; playId?: string },
): 'silent' | 'menu' | 'stale-origin' {
  if (places.length === 0) return origin.playId === undefined ? 'silent' : 'stale-origin';
  const originPlace = places.find(
    (place) =>
      place.playbookId === origin.playbookId &&
      place.rungId === origin.rungId &&
      (origin.playId === undefined || place.plays.some((play) => play.id === origin.playId)),
  );
  if (originPlace === undefined) return 'stale-origin';
  if (places.length === 1 && distinctUnsentPlays(places) <= 1) return 'silent';
  return 'menu';
}

/**
 * [R14][R20] The rep sentence a `'stale-origin'` result from
 * {@link inPlayUsageGate} is refused with — the group's own place is gone, so
 * there is nothing to save onto as the rep last saw it. Plan 2026-09-28-008
 * U2: nothing reloads on its own. `gateInPlayWrite` returns `'stale'` (no
 * request, no adopt) and every host opens the Save panel already stale with
 * this sentence (`staleRefusal`) and the rep's draft; only the rep's "Reload
 * to see their change" (`usage-sidebar-reload.ts`) refetches. Never a raw
 * id/status/path (R20).
 */
export const GROUP_MOVED_SENTENCE =
  'This group was changed by someone else since you loaded it. Check the group and try again.';

/**
 * R5-6/R5-7 (playbooks-qa-round5-feedback.md) — the LIBRARY/Config doorway's
 * own threshold, deliberately different from {@link inPlayUsageGate}.
 *
 * `places` (`getSequenceUsage`/`getSegmentationUsage`) never counts the row
 * being edited itself — there is no rung/mapping concept on the Library page
 * to BE a "place". From the RUNG doorway (`SequencesStep.tsx`,
 * `SegmentationConditionsEditor` mounted inside `QualifyStep`), the rung
 * being edited IS one of the counted places, so `places.length > 1` correctly
 * means "at least one place besides this one". From the Library doorway,
 * `places.length === 1` ALREADY means "used somewhere else besides this
 * library definition" — the `>1` rule silently wrote a shared object in
 * place because it was measuring the wrong thing for this doorway, not
 * because the wiring was missing.
 *
 * Either axis alone triggers it, same "no axis alone suppresses it" rule
 * {@link inPlayUsageGate} follows — `places.length > 1 &&
 * distinctUnsentPlays <= 1` (today's rung threshold) would still miss the
 * off-by-one this exists to fix.
 */
export function libraryUsageGateNeedsConfirmation(places: readonly PlaybooksUsagePlace[]): boolean {
  return places.length > 0 || distinctUnsentPlays(places) > 0;
}

/**
 * Decision 7 (S1b) — the discoverability copy shared by the editor header
 * badge, the Generate drawer's review footer, and the apply toast suffix.
 * `null` for 0 or 1 (a badge/footer line reading "Used in 1 place" for a
 * sequence's own sole use is noise, not information) so every call site can
 * render `{usedInPlacesText(n) && <Whatever>{usedInPlacesText(n)}</Whatever>}`
 * without its own threshold check.
 */
export function usedInPlacesText(count: number): string | null {
  return count > 1 ? `Used in ${count} places` : null;
}
